import React, { createContext, useState, useContext, useEffect } from 'react';
import { projects as projectsApi } from '../services/api';
import { useAuth } from './AuthContext';

const ProjectContext = createContext();

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load projects from API
  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsApi.getAll();
      const projectList = response.data;
      setProjects(projectList);

      // Try to restore previously selected project from localStorage
      const savedProjectId = localStorage.getItem('selectedProjectId');
      if (savedProjectId) {
        const savedProject = projectList.find(p => p.id === parseInt(savedProjectId));
        if (savedProject) {
          setSelectedProject(savedProject);
        } else if (projectList.length > 0) {
          // If saved project not found, select first project
          selectProject(projectList[0]);
        }
      } else if (projectList.length > 0) {
        // No saved project, select first one
        selectProject(projectList[0]);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  // Select a project and persist to localStorage
  const selectProject = (project) => {
    setSelectedProject(project);
    if (project) {
      localStorage.setItem('selectedProjectId', project.id.toString());
    } else {
      localStorage.removeItem('selectedProjectId');
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedProject(null);
    localStorage.removeItem('selectedProjectId');
  };

  // Refresh projects list (useful after CRUD operations)
  const refreshProjects = async () => {
    await loadProjects();
  };

  // Load projects when user logs in
  useEffect(() => {
    if (user) {
      loadProjects();
    } else {
      // Clear state when user logs out
      setProjects([]);
      setSelectedProject(null);
      setLoading(false);
    }
  }, [user]);

  const value = {
    projects,
    selectedProject,
    selectProject,
    clearSelection,
    refreshProjects,
    loading
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};
